<script setup>
import { computed, watch, ref, onMounted, getCurrentInstance } from 'vue'
import PaywallService from "@/services/PaywallService";
import IconBanknote from "../../components/icon/IconBanknote.vue"
import IconChevronRight from "../../components/icon/IconChevronRight.vue"
import IconBin from "../../components/icon/IconBin.vue"
import FormSwitch from "../../components/Atoms/FormSwitch.vue"
import { useStore } from 'vuex'
import { useNotification } from "@kyvg/vue3-notification";
import FormButton from '../Atoms/FormButton.vue';
import FormUpload from '../Atoms/FormUpload.vue';
import IconBase from '../icon/IconBase.vue';
import IconBookOpen from '../icon/IconBookOpen.vue';
import IconElipsisVertical from '@/components/icon/IconElipsisVertical.vue'
import { useRoute, useRouter } from 'vue-router'
import DropDownMenu from '@/components/Atoms/DropDownMenu.vue';
import DropDownMenuItem from '@/components/Atoms/DropDownMenuItem.vue';
import Title from '../ui/Title.vue'
import Alert from '../Atoms/Alert.vue';

const instance = getCurrentInstance();
const root = instance.proxy.$root;
const route = useRoute()
const router = useRouter()
const store = useStore()
const { notify } = useNotification()

const posterSrc = ref(null)
const posterProcessing = ref(false)
const posterRemoving = ref(false)
const posterError = ref(null)

// Reset the file input so re-selecting the same file fires @change again.
const resetPosterInput = () => {
  const input = document.getElementById('poster-image-input')
  if (input) input.value = ''
}

const userPaywall = computed(() => store.getters['User/userPaywall'])
const paywalls = computed(() => store.getters['Paywalls/paywalls'])
const hasLiveSubscription = computed(() => store.getters['User/hasLiveSubscription'])
const isLiveSubscriptionExpired = computed(() => store.getters['User/isLiveSubscriptionExpired'])

const props = defineProps({
  currentPaywall: {
    type: Function,
  }
})

const emit = defineEmits({
  updated: () => true,
})

const onPosterChange = async (file) => {
  if (!file) return
  if (!['image/png', 'image/jpeg'].includes(file.type)) {
    posterError.value = 'Please select a PNG or JPEG image.'
    return
  }
  if (file.size > 5 * 1024 * 1024) {
    posterError.value = 'Poster must be under 5 MB.'
    return
  }
  posterError.value = null
  posterProcessing.value = true
  const fdata = new FormData()
  fdata.append('file', file)
  try {
    const res = await PaywallService.paywallUploadPoster(fdata, props.currentPaywall.id)
    posterSrc.value = res.url || res.logoUrl
    store.dispatch('Paywalls/setPaywallProp', { id: props.currentPaywall.id, prop: 'wallpaper_url', value: posterSrc.value })
    emit('updated')
    notify({ group: 'success', text: 'Poster updated' })
  } catch (err) {
    posterError.value = "Couldn't upload the poster. Please try again."
    notify({ group: 'error', text: err.message || "Couldn't upload poster" })
  } finally {
    posterProcessing.value = false
    resetPosterInput()
  }
}

const removePoster = async () => {
  posterRemoving.value = true
  try {
    await PaywallService.updateStreamPaywallById(props.currentPaywall.id, { wallpaper_url: null })
    posterSrc.value = null
    store.dispatch('Paywalls/setPaywallProp', { id: props.currentPaywall.id, prop: 'wallpaper_url', value: null })
    resetPosterInput()
    emit('updated')
    notify({ group: 'success', text: 'Custom poster removed — using the content source poster' })
  } catch (err) {
    notify({ group: 'error', text: err.message || "Couldn't remove poster" })
  } finally {
    posterRemoving.value = false
  }
}

onMounted(() => {
  posterSrc.value = props.currentPaywall?.wallpaper_url
})
</script>

<template>
  <section class="bg-surface-1 rounded-xl p-4 sm:p-5">
    <div class="mb-4">
      <h3 class="text-sm font-semibold text-white mb-0.5">Appearance</h3>
      <p class="text-tiny text-surface-7 mb-0">Control how your paywall appears to viewers.</p>
    </div>

    <!-- Poster -->
    <div class="mb-4">
      <h4 class="text-sm font-medium text-white mb-0.5">Poster image</h4>
      <p class="text-tiny text-surface-7 mb-0">Displayed behind the paywall before playback. Inherits the poster from its
        <a v-if="props.currentPaywall?.source" class="text-main hover:underline" target="_blank"
          :href="(props.currentPaywall?.source?.type == 'live') ? `/app/livestreams/${props.currentPaywall?.source?._id}/settings/player-interface` : `/app/vods/${props.currentPaywall?.source?._id}/videos`">content
          source</a>
        <span v-else>content source</span> — upload an image to override it.
      </p>
      <div class="flex gap-x-2 items-center w-full mt-2">
        <img v-if="posterSrc" :src="posterSrc" alt="Paywall poster"
          class="h-[54px] w-[96px] object-cover rounded bg-surface-2" />
        <p v-else class="text-tiny text-surface-6">Inheriting poster from content source</p>
        <FormUpload class="ml-auto" name="paywallPoster" id="poster-image-input" placeholder="No file chosen"
          acceptType="image/png,image/jpeg"
          :label="posterProcessing ? 'Uploading…' : (posterSrc ? 'Change' : 'Upload poster')" type="secondary" size="sm"
          :disabled="posterProcessing || posterRemoving" :on-file-change="onPosterChange" />
        <FormButton v-if="posterSrc" :disabled="posterProcessing || posterRemoving" type="secondary" size="sm"
          @click="removePoster">
          {{ posterRemoving ? 'Removing…' : 'Remove' }}
        </FormButton>
      </div>
      <Alert type="error" v-show="posterError" :text="posterError" class="mt-2" />
    </div>
  </section>
</template>

<style scoped></style>
